import { Link } from 'react-router-dom';
import { motion } from 'motion/react';
import { Section, SectionItem, SectionHeader, SEO } from '@/components';
import { caseStudies, guides, webinars } from '@/data/resources';

export function ResourcesPage() {
  return (
    <>
      <SEO
        title="Resources - Guides, Case Studies & Webinars"
        description="Free resources to help grow your service business. Download guides, read case studies, and watch webinars on dispatch, operations, and scaling your service company."
        keywords={[
          'field service resources',
          'service business guides',
          'HVAC case studies',
          'plumbing business tips',
          'service company webinars',
          'business growth resources',
        ]}
        ogImage="/og/resources.png"
        type="website"
      />

      {/* Hero Section */}
      <section className="bg-ink pt-32 pb-20">
        <div className="container-marketing">
          <div className="max-w-3xl mx-auto text-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
            >
              <span className="inline-block px-4 py-1.5 rounded-full bg-accent/10 text-accent text-sm font-medium mb-6">
                Free Resources
              </span>
              <h1 className="font-display text-h1 text-paper mb-6">
                Resources to Grow Your Service Business
              </h1>
              <p className="text-xl text-gray-300 mb-8">
                Case studies, guides, and webinars from industry experts. Everything you need to
                run a more efficient, profitable service company.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Case Studies Section */}
      <Section background="paper">
        <SectionHeader
          title="Case Studies"
          subtitle="See how real service companies transformed their operations with Omniforge"
        />
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {caseStudies.map((study) => (
            <SectionItem key={study.id}>
              <CaseStudyCard study={study} />
            </SectionItem>
          ))}
        </div>
      </Section>

      {/* Guides Section */}
      <Section background="cream">
        <SectionHeader
          title="Guides & eBooks"
          subtitle="In-depth resources to help you master every aspect of running a service business"
        />
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {guides.map((guide) => (
            <SectionItem key={guide.id}>
              <GuideCard guide={guide} />
            </SectionItem>
          ))}
        </div>
      </Section>

      {/* Webinars Section */}
      <Section background="paper">
        <SectionHeader
          title="Webinars"
          subtitle="Learn from industry experts in live and on-demand sessions"
        />
        <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
          {webinars.map((webinar) => (
            <SectionItem key={webinar.id}>
              <WebinarCard webinar={webinar} />
            </SectionItem>
          ))}
        </div>
      </Section>

      {/* Newsletter CTA */}
      <Section background="ink">
        <div className="max-w-2xl mx-auto text-center">
          <SectionItem>
            <h2 className="font-display text-h2 text-paper mb-4">
              Get the latest resources in your inbox
            </h2>
            <p className="text-gray-300 mb-8">
              Subscribe to our newsletter for weekly tips, guides, and industry insights.
              No spam, just actionable advice.
            </p>
            <NewsletterForm />
          </SectionItem>
        </div>
      </Section>
    </>
  );
}

// ============================================
// CASE STUDY CARD COMPONENT
// ============================================

interface CaseStudyCardProps {
  study: (typeof caseStudies)[0];
}

function CaseStudyCard({ study }: CaseStudyCardProps) {
  return (
    <Link
      to={`/case-study/${study.slug}`}
      className="group block bg-white rounded-2xl overflow-hidden shadow-soft hover:shadow-elevated transition-all duration-300 hover:-translate-y-1"
    >
      <div className="relative aspect-[3/2] overflow-hidden">
        <img
          src={study.imageUrl}
          alt={study.company}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
        />
        <div className="absolute top-4 left-4">
          <span className="px-3 py-1 bg-white/90 backdrop-blur-sm rounded-full text-xs font-medium">
            {study.industry}
          </span>
        </div>
      </div>
      <div className="p-6">
        <h3 className="font-heading font-semibold text-lg mb-2 group-hover:text-accent transition-colors">
          {study.title}
        </h3>
        <p className="text-muted text-sm mb-4 line-clamp-2">{study.excerpt}</p>
        <div className="flex gap-4">
          {study.results.slice(0, 2).map((result, i) => (
            <div key={i} className="text-center">
              <div className="text-xl font-bold text-accent">{result.value}</div>
              <div className="text-xs text-muted">{result.label}</div>
            </div>
          ))}
        </div>
      </div>
    </Link>
  );
}

// ============================================
// GUIDE CARD COMPONENT
// ============================================

interface GuideCardProps {
  guide: (typeof guides)[0];
}

function GuideCard({ guide }: GuideCardProps) {
  const categoryColors: Record<string, string> = {
    growth: 'bg-green-100 text-green-800',
    operations: 'bg-blue-100 text-blue-800',
    technology: 'bg-purple-100 text-purple-800',
    leadership: 'bg-orange-100 text-orange-800',
  };

  return (
    <Link
      to={guide.downloadUrl}
      className="group block bg-white rounded-2xl overflow-hidden shadow-soft hover:shadow-elevated transition-all duration-300 hover:-translate-y-1"
    >
      <div className="relative aspect-[4/3] overflow-hidden bg-gray-100">
        <img
          src={guide.imageUrl}
          alt={guide.title}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
        <div className="absolute bottom-4 left-4 right-4">
          <span className={`px-3 py-1 rounded-full text-xs font-medium ${categoryColors[guide.category]}`}>
            {guide.category.charAt(0).toUpperCase() + guide.category.slice(1)}
          </span>
        </div>
      </div>
      <div className="p-6">
        <h3 className="font-heading font-semibold text-lg mb-2 group-hover:text-accent transition-colors">
          {guide.title}
        </h3>
        <p className="text-muted text-sm mb-4 line-clamp-2">{guide.description}</p>
        <div className="flex items-center justify-between">
          <span className="text-sm text-muted">{guide.pageCount} pages</span>
          <span className="flex items-center gap-2 text-accent font-medium text-sm group-hover:gap-3 transition-all">
            Download Free
            <DownloadIcon className="w-4 h-4" />
          </span>
        </div>
      </div>
    </Link>
  );
}

// ============================================
// WEBINAR CARD COMPONENT
// ============================================

interface WebinarCardProps {
  webinar: (typeof webinars)[0];
}

function WebinarCard({ webinar }: WebinarCardProps) {
  const formattedDate = new Date(webinar.date).toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });

  return (
    <Link
      to={webinar.registrationUrl}
      className="group block bg-white rounded-2xl overflow-hidden shadow-soft hover:shadow-elevated transition-all duration-300 hover:-translate-y-1"
    >
      <div className="relative aspect-video overflow-hidden">
        <img
          src={webinar.imageUrl}
          alt={webinar.title}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
        />
        {webinar.isUpcoming && (
          <div className="absolute top-4 left-4">
            <span className="px-3 py-1 bg-accent text-white rounded-full text-xs font-medium">
              Upcoming
            </span>
          </div>
        )}
        <div className="absolute inset-0 flex items-center justify-center bg-black/20 opacity-0 group-hover:opacity-100 transition-opacity">
          <div className="w-16 h-16 rounded-full bg-white/90 flex items-center justify-center">
            <PlayIcon className="w-6 h-6 text-accent ml-1" />
          </div>
        </div>
      </div>
      <div className="p-6">
        <div className="flex items-center gap-2 text-sm text-muted mb-3">
          <span>{formattedDate}</span>
          <span>|</span>
          <span>{webinar.duration}</span>
        </div>
        <h3 className="font-heading font-semibold text-lg mb-3 group-hover:text-accent transition-colors">
          {webinar.title}
        </h3>
        <p className="text-muted text-sm mb-4 line-clamp-2">{webinar.description}</p>
        <div className="flex items-center gap-3">
          <img
            src={webinar.speakerImage}
            alt={webinar.speaker}
            className="w-10 h-10 rounded-full object-cover"
          />
          <div>
            <div className="font-medium text-sm">{webinar.speaker}</div>
            <div className="text-xs text-muted">{webinar.speakerRole}</div>
          </div>
        </div>
      </div>
    </Link>
  );
}

// ============================================
// NEWSLETTER FORM COMPONENT
// ============================================

function NewsletterForm() {
  return (
    <form className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
      <input
        type="email"
        placeholder="Enter your email"
        className="flex-1 px-4 py-3 rounded-lg bg-charcoal border border-gray-700 text-paper placeholder-gray-400 focus:outline-none focus:border-accent transition-colors"
      />
      <button
        type="submit"
        className="btn-primary px-6 py-3 whitespace-nowrap"
      >
        Subscribe
      </button>
    </form>
  );
}

// ============================================
// ICONS
// ============================================

function DownloadIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M7 10l5 5 5-5M12 15V3" />
    </svg>
  );
}

function PlayIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
      <path d="M8 5v14l11-7z" />
    </svg>
  );
}
